import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KnowledgeGraph = ({ onClose }) => {
  const canvasRef = useRef(null);
  const [graphData, setGraphData] = useState({ nodes: [], edges: [], clusters: [] });
  const [loading, setLoading] = useState(true);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [nodePositions, setNodePositions] = useState([]);
  const animRef = useRef(null);

  useEffect(() => {
    const fetchGraph = async () => {
      try {
        const res = await axios.get(`${API}/bookmarks/knowledge-graph`);
        setGraphData(res.data);
      } catch (e) {
        toast.error("Failed to load knowledge graph");
      } finally {
        setLoading(false);
      }
    };
    fetchGraph();
  }, []);

  // Force-directed layout simulation
  const initPositions = useCallback((nodes) => {
    const w = 800, h = 600;
    return nodes.map((node, i) => {
      const angle = (2 * Math.PI * i) / nodes.length;
      const r = Math.min(w, h) * 0.3;
      return {
        ...node,
        x: w / 2 + r * Math.cos(angle) + (Math.random() - 0.5) * 60,
        y: h / 2 + r * Math.sin(angle) + (Math.random() - 0.5) * 60,
        vx: 0,
        vy: 0,
      };
    });
  }, []);

  useEffect(() => {
    if (graphData.nodes.length < 2) return;
    let positions = initPositions(graphData.nodes);
    setNodePositions(positions);

    // Run force simulation for 120 iterations
    const edgeMap = graphData.edges.map(e => ({
      si: graphData.nodes.findIndex(n => n.id === e.source),
      ti: graphData.nodes.findIndex(n => n.id === e.target),
      weight: e.weight,
    })).filter(e => e.si >= 0 && e.ti >= 0);

    for (let iter = 0; iter < 120; iter++) {
      const alpha = 0.3 * (1 - iter / 120);
      // Repulsion
      for (let i = 0; i < positions.length; i++) {
        for (let j = i + 1; j < positions.length; j++) {
          const dx = positions[j].x - positions[i].x;
          const dy = positions[j].y - positions[i].y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const force = 2000 / (dist * dist);
          const fx = (dx / dist) * force * alpha;
          const fy = (dy / dist) * force * alpha;
          positions[i].vx -= fx;
          positions[i].vy -= fy;
          positions[j].vx += fx;
          positions[j].vy += fy;
        }
      }
      // Attraction along edges
      for (const e of edgeMap) {
        const dx = positions[e.ti].x - positions[e.si].x;
        const dy = positions[e.ti].y - positions[e.si].y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const force = (dist - 100) * 0.05 * e.weight * alpha;
        const fx = (dx / dist) * force;
        const fy = (dy / dist) * force;
        positions[e.si].vx += fx;
        positions[e.si].vy += fy;
        positions[e.ti].vx -= fx;
        positions[e.ti].vy -= fy;
      }
      // Center gravity
      for (let i = 0; i < positions.length; i++) {
        positions[i].vx += (400 - positions[i].x) * 0.005 * alpha;
        positions[i].vy += (300 - positions[i].y) * 0.005 * alpha;
        positions[i].x += positions[i].vx;
        positions[i].y += positions[i].vy;
        positions[i].vx *= 0.9;
        positions[i].vy *= 0.9;
        // Bounds
        positions[i].x = Math.max(40, Math.min(760, positions[i].x));
        positions[i].y = Math.max(40, Math.min(560, positions[i].y));
      }
    }
    setNodePositions([...positions]);
  }, [graphData, initPositions]);

  // Canvas render
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || nodePositions.length === 0) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    canvas.width = 800 * dpr;
    canvas.height = 600 * dpr;
    ctx.scale(dpr, dpr);

    const draw = () => {
      ctx.clearRect(0, 0, 800, 600);
      ctx.fillStyle = "#0A0600";
      ctx.fillRect(0, 0, 800, 600);

      // Draw edges
      for (const edge of graphData.edges) {
        const src = nodePositions.find(n => n.id === edge.source);
        const tgt = nodePositions.find(n => n.id === edge.target);
        if (!src || !tgt) continue;
        ctx.strokeStyle = `rgba(232, 160, 48, ${Math.min(edge.weight * 0.8, 0.4)})`;
        ctx.lineWidth = edge.weight * 3;
        ctx.beginPath();
        ctx.moveTo(src.x, src.y);
        ctx.lineTo(tgt.x, tgt.y);
        ctx.stroke();
      }

      // Draw nodes
      for (const node of nodePositions) {
        const isHovered = hoveredNode?.id === node.id;
        const r = isHovered ? 10 : 7;

        // Glow
        const gradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, r * 3);
        gradient.addColorStop(0, node.collection_color + "30");
        gradient.addColorStop(1, "transparent");
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(node.x, node.y, r * 3, 0, Math.PI * 2);
        ctx.fill();

        // Node circle
        ctx.fillStyle = node.collection_color || "#E8A030";
        ctx.beginPath();
        ctx.arc(node.x, node.y, r, 0, Math.PI * 2);
        ctx.fill();

        // Border
        ctx.strokeStyle = isHovered ? "#FDF8EE" : "rgba(253,248,238,0.3)";
        ctx.lineWidth = isHovered ? 2 : 0.5;
        ctx.stroke();

        // Label
        ctx.font = `${isHovered ? "bold " : ""}9px 'DM Mono', monospace`;
        ctx.fillStyle = isHovered ? "#FDF8EE" : "#8C7A5A";
        ctx.textAlign = "center";
        ctx.fillText(node.label, node.x, node.y + r + 12);
      }
    };

    draw();
    animRef.current = draw;
  }, [nodePositions, hoveredNode, graphData.edges]);

  const handleMouseMove = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mx = (e.clientX - rect.left) * (800 / rect.width);
    const my = (e.clientY - rect.top) * (600 / rect.height);

    let found = null;
    for (const node of nodePositions) {
      const dx = mx - node.x;
      const dy = my - node.y;
      if (dx * dx + dy * dy < 225) {
        found = node;
        break;
      }
    }
    setHoveredNode(found);
  }, [nodePositions]);

  return (
    <div className="fixed inset-0 z-50 bg-[rgba(0,0,0,0.8)] flex items-center justify-center p-4" data-testid="knowledge-graph-modal">
      <div className="bg-[#1C1004] border border-[rgba(232,160,48,0.15)] max-w-[860px] w-full">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[rgba(232,160,48,0.1)]">
          <div className="flex items-center gap-3">
            <span className="text-[#E8A030]">⬡</span>
            <h2 className="font-serif font-bold text-[#FDF8EE]">Knowledge Graph</h2>
            <span className="font-mono text-[8px] text-[#4A3A1A] uppercase tracking-[1px]">
              {graphData.nodes.length} nodes · {graphData.edges.length} connections
            </span>
          </div>
          <button onClick={onClose} className="text-[#8C7A5A] hover:text-[#FDF8EE] transition-colors text-lg" data-testid="close-graph-btn">
            &times;
          </button>
        </div>

        <div className="relative">
          {loading ? (
            <div className="flex items-center justify-center h-[600px]">
              <div className="spinner-ember" />
            </div>
          ) : graphData.nodes.length < 2 ? (
            <div className="flex flex-col items-center justify-center h-[400px] text-center">
              <span className="text-3xl text-[#4A3A1A] mb-4">⬡</span>
              <p className="text-[#8C7A5A]">Save more entries to see topic connections.</p>
            </div>
          ) : (
            <>
              <canvas
                ref={canvasRef}
                style={{ width: "100%", height: "auto", aspectRatio: "800/600", cursor: hoveredNode ? "pointer" : "default" }}
                onMouseMove={handleMouseMove}
                data-testid="knowledge-graph-canvas"
              />
              {/* Hover tooltip */}
              {hoveredNode && (
                <div className="absolute bottom-4 left-4 right-4 bg-[#0A0600] border border-[rgba(232,160,48,0.15)] p-3 pointer-events-none">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-2 h-2 rounded-full" style={{ background: hoveredNode.collection_color }} />
                    <span className="font-serif font-bold text-sm text-[#FDF8EE]">{hoveredNode.label}</span>
                    <span className="font-mono text-[8px] text-[#4A3A1A] uppercase tracking-[1px]">{hoveredNode.collection_name}</span>
                  </div>
                  <p className="text-[11px] text-[#8C7A5A] leading-[1.5]">{hoveredNode.content_preview}...</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Legend */}
        {graphData.clusters.length > 0 && (
          <div className="flex flex-wrap gap-4 px-5 py-3 border-t border-[rgba(232,160,48,0.1)]">
            {graphData.clusters.map((c, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: c.color }} />
                <span className="font-mono text-[8px] text-[#8C7A5A] uppercase tracking-[1px]">{c.name} ({c.count})</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export { KnowledgeGraph };
